import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import has from 'lodash/has';
import capitalize from 'lodash/capitalize';
import Axios from 'axios';
import { getTime_mmss } from '../../../helpers/formatTime';
import Colors from '../../../styles/colors';
import { OrangeSubmitButton } from '../../../components/buttons/buttons';
import Loading from '../../../components/Loading';
import ProgressRing from './ProgressRing';
import isNull from 'lodash/isNull';

class EpisodePreview extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      episode: {},
      listens: 0,
      liked: props.liked,
      descriptionShown: false,
      notesShown: false,
      actionstepShown: false,
      playingDuration: 0,
      playingCurrentTime: 0,
    };

    this.handlePlay = this.handlePlay.bind(this);
    this.likeEpisode = this.likeEpisode.bind(this);
    this.audioTimeUpdateListener = this.audioTimeUpdateListener.bind(this);
  }

  componentDidMount() {
    if (!isEmpty(this.props.episode) && has(this.props, 'episode.title')) {
      this.setEpisodeData();
    }

    this.props.audioPlayer.addEventListener('timeupdate', this.audioTimeUpdateListener);
  }

  componentWillUnmount() {
    this.props.audioPlayer.removeEventListener('timeupdate', this.audioTimeUpdateListener);
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.episode, nextProps.episode)) {
      this.setEpisodeData(nextProps);
    }
  }

  audioTimeUpdateListener() {
    const { duration, currentTime } = this.props.audioPlayer;
    this.setState({
      playingDuration: duration,
      playingCurrentTime: currentTime,
    });
  }

  setEpisodeData(props = {}) {
    const { episode } = !isEmpty(props) ? props : this.props;
    this.setState({ episode }, () => {
      // get actual listens from api
      const { episodeId, id: episodeID } = episode || {};
      const id = episodeId || episodeID || '';
      if (!isEmpty(id)) {
        Axios.get(`/api/total_listens/${id}`).then(response => {
          const { status, data: { totalListens = 0 } } = response || {};
          const listens = (status === 200) ? totalListens : 0;
          this.setState({ listens });
        });
      } else {
        this.setState({ listens: 0 });
      }
    });
  }

  renderNotes(fileurl, episodeId) {
    if (!isEmpty(fileurl)) {
      if (fileurl.includes('.pdf')) {
        return (
          <div className="col-md-3 col-lg-3 col-sm-12 no-padding">
            <OrangeSubmitButton
              label="Download"
              onClick={() => (window.location = `/api/download_notes?episodeId=${episodeId}`)}
              styles={{ width: '100%', margin: 0 }}
            />
          </div>
        );
      } else if (fileurl.match(/\.(jpeg|jpg|gif|png)$/) !== null) {
        return <img src={fileurl} alt="image" />;
      }
    }
  }

  renderHTMLMarkup(markup) {
    return <div dangerouslySetInnerHTML={{ __html: markup }} />;
  }

  likeEpisode() {
    const { episode } = this.state;
    this.props.onEpisodeLike(episode);
  }

  handlePlay(episode) {
    return () => {
      this.props.onEpisodePlay(episode);
    };
  }

  handleMaterials(key) {
    const currentKey = key + 'Shown';
    const currentState = this.state[currentKey];
    this.setState({
      descriptionShown: false,
      notesShown: false,
      actionstepShown: false,
      [currentKey]: !currentState,
    });
  }

  renderMaterials(episode) {
    const { descriptionShown, notesShown, actionstepShown } = this.state;
    const { description = null, notes = null, actionstep = null, episodeId } = episode || {};
    if (isEmpty(description) && isEmpty(notes) && isEmpty(actionstep)) {
      return null;
    }

    const materialList = [];
    if (!isEmpty(description)) {
      materialList.push({
        title: 'Description',
        key: 'description',
        condition: descriptionShown,
        content: this.renderHTMLMarkup(description),
      });
    }
    if (!this.props.disableTracksAttachedContent && !isEmpty(notes) && !isEmpty(episodeId)) {
      materialList.push({
        title: 'Notes',
        key: 'notes',
        condition: notesShown,
        content: this.renderNotes(notes, episodeId),
      });
    }
    if (!isEmpty(actionstep)) {
      materialList.push({
        title: 'Actions',
        key: 'actionstep',
        condition: actionstepShown,
        content: episode.actionstep,
      });
    }

    return (
      <React.Fragment>
        <div className="padding-top-20px">
          {materialList.map(({ key, condition, title }, index) => {
            if (episode[key]) {
              let btnIconClass = condition ? 'fa fa-chevron-up' : 'fa fa-chevron-down';
              return (
                <HiddenTrigger
                  onClick={this.handleMaterials.bind(this, key)}
                  key={`${key}-${index}`}>
                  {capitalize(title)} <i className={btnIconClass} aria-hidden="true" />
                </HiddenTrigger>
              );
            }

            return null;
          })}
        </div>
        <div className="margin-three-top margin-three-bottom">
          {materialList.map(({ key, condition, content }, index) => {
            return (
              <div
                className="row"
                key={`${key}-${index}`}
                style={{ display: condition ? 'block' : 'none' }}>
                <div className="col-lg-11 col-md-11 col-lg-11 col-sm-11">
                  <Materials className="text-large text-dark-gray sm-text-large xs-text-large">
                    {content}
                  </Materials>
                </div>
              </div>
            );
          })}
        </div>
      </React.Fragment>
    );
  }

  convertToTime(number) {
    const mins = Math.floor(number / 60);
    const secs = (number % 60).toFixed();
    return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  renderPlayingIndicator() {
    const { episode, playingCurrentTime, playingDuration } = this.state;
    const { playing, paused, loading, currentEpisode } = this.props;
    const isCurrentEpisode = currentEpisode && episode.episodeId == currentEpisode.episodeId;
    const isPlayingCurrentEpisode = playing && isCurrentEpisode;
    const isPausedCurrentEpisode = paused && isCurrentEpisode;
    const isPlayingOrPaused = isPlayingCurrentEpisode || isPausedCurrentEpisode;

    const progress = playingCurrentTime / playingDuration;
    const timeLeft = playingDuration - playingCurrentTime;

    return (
      <TrackAction active={isPlayingOrPaused} onClick={this.handlePlay(episode)}>
        <ProgressRing
          radius={40}
          strokeColor={Colors.darkGrey}
          strokeWidth={8}
          progress={isCurrentEpisode && !isNaN(progress) ? progress * 100 : 0}
        />
        <div className="track-pause-play">
          {!loading && (
            <i className="material-icons play" aria-hidden="true">
              {isPlayingCurrentEpisode ? 'pause_circle_filled' : 'play_circle_filled'}
            </i>
          )}
          {loading && <Loading align="center" size={52} type="circular" />}
          {episode.duration && (
            <div className="track-duration">
              {isPlayingOrPaused && !isNaN(timeLeft)
                ? this.convertToTime(timeLeft)
                : getTime_mmss(episode.duration)}
            </div>
          )}
        </div>
      </TrackAction>
    );
  }

  render() {
    const { episode, listens } = this.state;
    const {
      liked,
      soundcast: { disableComment = false, showListenCount = true, showTimeStamps = true },
    } = this.props;

    const {
      likesCount: likes = 0,
      date_published: datePublished = null,
      createdAt: dateCreatedNew = null,
      date_created: dateCreatedOld = null,
    } = episode || {};
    const dateCreated = !isNull(datePublished) ? moment.unix(datePublished)
      : !isNull(dateCreatedOld) ? moment.unix(dateCreatedOld)
      : !isNull(dateCreatedNew) ? moment(dateCreatedNew) : moment();
    const dateCreatedTxt = dateCreated.year() === moment().year()
      ? dateCreated.format('ddd MMM DD')
      : dateCreated.format('ddd MMM DD, yyyy')

    return (
      <div className="list-group">
        <div className="list-group-item border-none margin-top-10px margin-bottom-10px">
          <div className="row">
            <div className="col-lg-2 col-md-2 col-sm-2 col-xs-4">
              <PlayingIndicator>{this.renderPlayingIndicator()}</PlayingIndicator>
            </div>
            <div className="col-lg-10 col-md-10 col-sm-10 col-xs-8 padding-top-20px padding-bottom-20px">
              <div className="title-small sm-title-medium xs-title-medium">{episode.title}</div>
              <div className="margin-three-bottom margin-three-top tz-text">
                <StatsWrapper>
                  {showTimeStamps && (
                    <Stats className="text-large sm-title-small xs-title-small font-weight-400 alt-font xs-padding-five-bottom">
                      {`${dateCreatedTxt}`}
                    </Stats>
                  )}
                  {!disableComment && (
                    <Stats
                      className="text-large sm-title-small xs-title-small font-weight-400 alt-font xs-padding-five-bottom likes"
                      liked={liked}
                      onClick={this.likeEpisode}>
                      <i className="material-icons" aria-hidden="true">
                        favorite
                      </i>
                      {` ${likes} ${likes > 1 ? 'likes' : 'like'} `}
                    </Stats>
                  )}
                  {showListenCount && (
                    <Stats className="text-large sm-title-small xs-title-small font-weight-400 alt-font xs-padding-five-bottom">
                      <i className="fa fa-headphones" aria-hidden="true" />
                      {` ${listens} ${listens > 1 ? 'listens' : 'listen'}`}
                    </Stats>
                  )}
                </StatsWrapper>
              </div>
              {this.renderMaterials(episode)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const TrackAction = styled.div`
  position: relative;
  cursor: pointer;

  .track-pause-play {
    position: absolute;
    top: 14px;
    left: 14px;

    .material-icons {
      &.play {
        color: ${props => (props.active ? Colors.mainOrange : Colors.link)};
        cursor: pointer;
        font-size: 4em;
      }
    }

    .track-duration {
      font-size: 1.5rem;
      text-align: center;
      width: 100%;
    }
  }
`;

const HiddenTrigger = styled.div`
  display: inline-block;
  font-size: 1.4rem;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  padding-right: 20px;

  i.fa {
    margin-left: 5px;
  }
`;

const PlayingIndicator = styled.div`
  display: flex;
  justify-content: center;
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Stats = styled.div`
  display: flex;
  flex-direction: row;
  width: 130px;
  align-items: center;

  i {
    margin-right: 5px;
  }

  &.likes {
    i {
      font-size: 16px;
      color: ${props => (props.liked ? 'red' : 'black')};
      cursor: pointer;
    }
  }
`;

const Materials = styled.div`
  white-space: pre-wrap.
  text-align: left;
  max-height: 400px;
  overflow-y: auto;
`;

EpisodePreview.propTypes = {
  episode: PropTypes.object,
  liked: PropTypes.bool,
  userInfo: PropTypes.object,
  onEpisodePlay: PropTypes.func,
  onEpisodeLike: PropTypes.func,
  currentEpisode: PropTypes.object,
  playing: PropTypes.bool,
  loading: PropTypes.bool,
  paused: PropTypes.bool,
  soundcast: PropTypes.object,
  soundcastID: PropTypes.string,
  audioPlayer: PropTypes.object,
};

EpisodePreview.defaultProps = {
  episode: {},
  liked: false,
  userInfo: {},
  onEpisodePlay: () => {},
  onEpisodeLike: () => {},
  currentEpisode: {},
  playing: false,
  loading: false,
  paused: false,
  soundcast: {},
  soundcastID: '',
  audioPlayer: {},
};

export default EpisodePreview;
